import { BodyXs } from "@/domains/design/components/body-xs"
import { cn, dollarsPrettified } from "@/domains/library"

/**
 * Five-tile stats card rendered inside the manage-position Overview tab.
 * Position value, claimable fees, claimed fees (with per-token breakdown),
 * lifetime deposits, and ROI.
 *
 * Pure view — consumes pre-formatted strings and numbers from the composite
 * so this file stays Storybook-able without MobX.
 */
export interface PositionStatsCardViewProps {
  positionValueUsd: number
  claimableFeesUsd: number
  claimableFeesToken0: number | undefined
  claimableFeesToken1: number | undefined
  earnedFeesUsd: number
  earnedFeesToken0: number
  earnedFeesToken1: number
  token0Symbol: string
  token1Symbol: string
  depositedToken0: number
  depositedToken1: number
  /** Decimal percentage, e.g. `12.34` — `undefined` renders `—`. */
  roiPct: number | undefined
  /** Decimal percentage — positive numbers show in green, negative in red. */
  netPnlUsd: number
  className?: string
}

export function PositionStatsCardView({
  positionValueUsd,
  claimableFeesUsd,
  claimableFeesToken0,
  claimableFeesToken1,
  earnedFeesUsd,
  earnedFeesToken0,
  earnedFeesToken1,
  token0Symbol,
  token1Symbol,
  depositedToken0,
  depositedToken1,
  roiPct,
  netPnlUsd,
  className,
}: PositionStatsCardViewProps) {
  // Keep tone aligned with what users actually see at 2dp USD precision.
  // Values that round to "$0.00" should stay neutral (white), not red/green.
  const netPnlForTone = Math.abs(netPnlUsd) < 0.01 ? 0 : netPnlUsd
  const pnlTone = netPnlForTone > 0 ? "up" : netPnlForTone < 0 ? "down" : "neutral"
  const roiTone = roiPct === undefined ? "neutral" : roiPct >= 0 ? "up" : "down"
  const claimableFeesSecondary =
    claimableFeesToken0 === undefined || claimableFeesToken1 === undefined
      ? undefined
      : `${formatAmount(claimableFeesToken0)} ${token0Symbol} · ${formatAmount(claimableFeesToken1)} ${token1Symbol}`
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5",
        className,
      )}
      data-testid="position-stats-card"
    >
      <StatTile label="Position value" primary={dollarsPrettified(positionValueUsd)} />
      <StatTile
        label="Claimable fees"
        primary={dollarsPrettified(claimableFeesUsd)}
        secondary={claimableFeesSecondary}
      />
      <StatTile
        label="Claimed fees"
        primary={dollarsPrettified(earnedFeesUsd)}
        secondary={`${formatAmount(earnedFeesToken0)} ${token0Symbol} · ${formatAmount(earnedFeesToken1)} ${token1Symbol}`}
      />
      <StatTile
        label="Deposits"
        primary={`${formatAmount(depositedToken0)} ${token0Symbol}`}
        secondary={`${formatAmount(depositedToken1)} ${token1Symbol}`}
      />
      <StatTile
        label="ROI"
        primary={formatPct(roiPct)}
        secondary={`Net PnL ${dollarsPrettified(netPnlUsd)}`}
        tone={roiTone}
        secondaryTone={pnlTone}
      />
    </div>
  )
}

function StatTile({
  label,
  primary,
  secondary,
  tone = "neutral",
  secondaryTone = "neutral",
}: {
  label: string
  primary: string
  secondary?: string
  tone?: "neutral" | "up" | "down"
  secondaryTone?: "neutral" | "up" | "down"
}) {
  return (
    <div className="flex flex-col rounded-lg border border-p400-light bg-p400 p-4">
      <BodyXs className="uppercase tracking-wide text-t300">{label}</BodyXs>
      <span
        className={cn(
          "mt-1 text-base font-medium",
          tone === "up" && "text-primary-400",
          tone === "down" && "text-error-text",
          tone === "neutral" && "text-t100",
        )}
      >
        {primary}
      </span>
      {secondary && (
        <span
          className={cn(
            "mt-0.5 text-xs text-t100",
            secondaryTone === "up" && "text-primary-400",
            secondaryTone === "down" && "text-error-text",
            secondaryTone === "neutral" && "text-t100",
          )}
        >
          {secondary}
        </span>
      )}
    </div>
  )
}

function formatAmount(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "0"
  const abs = Math.abs(value)
  const digits = abs >= 1_000 ? 0 : abs >= 1 ? 2 : 4
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

function formatPct(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  const sign = value >= 0 ? "+" : ""
  return `${sign}${value.toFixed(2)}%`
}
